import { useDocumentVisibility, useIntervalFn } from '@vueuse/core'
import { watch, type Ref } from 'vue'

import { PendingMatch, ScreenOrientation } from '@/enums/screen'
import i18n from '@/libs/i18n'
import { useAppStore } from '@/stores/app'
import { usePendingStore } from '@/stores/pending'
import type { IPendingScreen } from '@/types/Screen'

/*
 * PENDING SCREENS in the console (docs/API.md → Pending screens): the polling that keeps the
 * list fresh, and the words a card uses.
 */

/** Zasloni asks every 5 s (docs/API.md); the menu's badge and Pregled more calmly. */
export const PENDING_POLL_MS       = 5_000
export const PENDING_POLL_CALM_MS  = 30_000

/**
 * Keeps `usePendingStore` fresh for a tenant: asked once at once (and whenever the tenant
 * changes or the person turns out to be an admin there), then every `everyMs` while the page
 * is visible, and again as soon as it comes back. Stops with the component.
 */
export const usePendingPoll = (tid: Ref<string>, everyMs: number) => {
    const appStore   = useAppStore()
    const store      = usePendingStore()
    const visibility = useDocumentVisibility()

    const refresh = () => { void store.load(tid.value) }

    const { pause, resume } = useIntervalFn(refresh, everyMs, { immediate: false })

    // The admin check waits for GET /me: until then nobody is an admin of anything.
    const isAdmin = () => appStore.isAdmin(tid.value)

    watch([tid, isAdmin], refresh, { immediate: true })

    watch(visibility, (now, before) => {
        if (now !== 'visible') {
            pause()

            return
        }

        if (before !== undefined) refresh()
        resume()
    }, { immediate: true })

    return { refresh }
}

/** "Chrome · Android TV", or a plain "Neznana naprava". */
export const pendingDevice = (item: IPendingScreen): string => item.device || i18n.global.t('pending.unknown_device')

/** The icon of the kind of display the user agent names. */
export const pendingDeviceIcon = (item: IPendingScreen): string => {
    const device = item.device

    if (/TV|Tizen|webOS/.test(device)) return 'i-lucide-tv'
    if (/iOS|Android$/.test(device)) return 'i-lucide-smartphone'
    if (/iPadOS/.test(device)) return 'i-lucide-tablet'

    return item.orientation === ScreenOrientation.Portrait ? 'i-lucide-rectangle-vertical' : 'i-lucide-monitor'
}

/** "1920 × 1080", or '' when the display did not say. */
export const pendingResolution = (item: IPendingScreen): string => item.resolution.replace(/\s*x\s*/i, ' × ')

/** "Zaslon 3": the first free "Zaslon N" after the screens there are. */
export const suggestScreenName = (names: string[]): string => {
    const taken = new Set(names.map((name) => name.trim().toLowerCase()))
    const word  = i18n.global.t('pending.default_name')

    let n = names.length + 1

    while (taken.has(`${word} ${n}`.toLowerCase())) n++

    return `${word} ${n}`
}

/**
 * A display without a tenant that this console lists because it is on the network of one of
 * the tenant's admins (`/tv` opened in the same office): it is confirmed and turned away
 * in this tenant, like the tenant's own.
 */
export const onSameNetwork = (item: IPendingScreen): boolean => item.matched_by === PendingMatch.Network

/**
 * The short address to type on a display's remote: `ds.pms.imprion.dev/tv` — no scheme (the
 * TV's browser adds it), no tenant (the display appears by itself on an admin's network).
 */
export const shortPlayerAddress = (): string => `${window.location.host}/tv`

/** The link a display opens to wait in this tenant's console: `https://…/play?t=<slug>`. */
export const pairingLink = (slug: string): string => `${window.location.origin}/play?t=${encodeURIComponent(slug)}`
